import React from "react";
import "../../common/styles/list-style.css";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import {
  Filter,
  TextInput,
  useTranslate,
  useListContext,
  TopToolbar,
  sanitizeListRestProps,
  CreateButton,
  Datagrid,
  TextField,
  Responsive,
  SimpleList,
  Record,
} from "react-admin";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import GroupWorkOutlinedIcon from "@material-ui/icons/GroupWorkOutlined";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { Tooltip, useTheme, Theme } from "@material-ui/core";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import SecurityIcon from "@material-ui/icons/Security";
import TruckOutlineIcon from "../../../common/icons/TruckOutlineIcon";

const ListFilter = (props: any) => {
  useSearchFields(props, ["name"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="groups-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="groups-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

export const GroupsList = (props: IResourceListProps) => {
  const translate = useTranslate();
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.Group,
    (x) => x.list
  );

  const ListActions = (props: any) => {
    const { className, exporter, filters, maxResults, ...rest } = props;
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
        {permissions &&
          permissions[permissionNames.admin.groups.main] &&
          permissions[permissionNames.admin.groups.create] && (
            <CreateButton basePath={basePath} />
          )}
      </TopToolbar>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Name">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <ProtectedElement source="name">
              <TextField
                source="name"
                label="resources.admin-groups.labels.name"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Creator user display name">
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <SecurityIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="creatorUserDisplayName">
              <TextField
                source="creatorUserDisplayName"
                label="resources.admin-groups.labels.creatorUserDisplayName"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Driver names">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <TruckOutlineIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="driverNames">
              <ArrayField
                source="driverNames"
                record={record}
                sortable={false}
                label="resources.admin-groups.labels.drivers"
                displayedElementsCount={4}
                emptyArrayDisplay={translate(
                  "resources.admin-groups.labels.noDrivers"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Name">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <LocalShippingOutlinedIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="vehicleNames">
              <ArrayField
                record={record}
                source="vehicleNames"
                sortable={false}
                label="resources.admin-groups.labels.vehicles"
                displayedElementsCount={4}
                emptyArrayDisplay={translate(
                  "resources.admin-groups.labels.noVehicles"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={GroupWorkOutlinedIcon} />}
          className="content-list"
          title="resources.admin-groups.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="edit"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      hasShow={props.hasShow}
                      {...props}
                      record={record}
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.groups.main] &&
                        permissions[permissionNames.admin.groups.update]
                      }
                    >
                      {permissions &&
                        permissions[permissionNames.admin.groups.main] &&
                        permissions[permissionNames.admin.groups.delete] && (
                          <DeleteRecordMenuItem />
                        )}
                    </ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="name">
                  <TextField
                    source="name"
                    label="resources.admin-groups.labels.name"
                  />
                </ProtectedElement>
                <ProtectedElement source="creatorUserDisplayName">
                  <TextField
                    source="creatorUserDisplayName"
                    label="resources.admin-groups.labels.creatorUserDisplayName"
                  />
                </ProtectedElement>
                <ProtectedElement source="driverNames">
                  <ArrayField
                    source="driverNames"
                    sortable={false}
                    label="resources.admin-groups.labels.drivers"
                    displayedElementsCount={4}
                    emptyArrayDisplay={translate(
                      "resources.admin-groups.labels.noDrivers"
                    )}
                  />
                </ProtectedElement>
                <ProtectedElement source="vehicleNames">
                  <ArrayField
                    source="vehicleNames"
                    sortable={false}
                    label="resources.admin-groups.labels.vehicles"
                    displayedElementsCount={4}
                    emptyArrayDisplay={translate(
                      "resources.admin-groups.labels.noVehicles"
                    )}
                  />
                </ProtectedElement>
                <ActionsField
                  hasShow={props.hasShow}
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.groups.main] &&
                    permissions[permissionNames.admin.groups.update]
                  }
                >
                  {permissions &&
                    permissions[permissionNames.admin.groups.main] &&
                    permissions[permissionNames.admin.groups.delete] && (
                      <DeleteRecordMenuItem />
                    )}
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
